import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { getWorkerStats } from "../../services/service";
import {
  CarIcon,
  CheckedInIcon,
  CloseIcon,
  CoffeeIcon,
  PlainClockIcon,
  BackArrowIcon,
} from "../../assets/icons";
import { Spin } from "antd";

// Utility function to transform API data
const transformApiData = (apiData, timeZone) => {
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(parsed);
  };

  const hasActualCheckOut = apiData?.details?.some(
    (detail) => detail?.activity === "CHECKED" && detail?.end_time
  );

  const transformedData = [
    {
      label: "Checked In:",
      icon: CheckedInIcon,
      time: formatDateTime(apiData?.checkInTime),
      type: "CHECK_IN",
    },
    {
      label: "Checked Out:",
      icon: CheckedInIcon,
      time: formatDateTime(hasActualCheckOut ? apiData?.checkOutTime : null),
      type: "CHECK_OUT",
    },
    {
      label: "Total Break Time:",
      icon: CoffeeIcon,
      time: apiData?.totalBreakTime || "N/A",
      type: "BREAK_TIME",
    },
    {
      label: "Total Travel Time:",
      icon: CarIcon,
      time: apiData?.totalTravelTime || "N/A",
      type: "TRAVEL_TIME",
    },
    {
      label: "Total Working Hours:",
      icon: PlainClockIcon,
      time: apiData?.totalWorkingTime || "N/A",
      type: "WORKING_TIME",
    },
  ];

  // Group break times
  const breakSummary = apiData?.details
    ?.filter(
      (detail) => detail?.type === "BREAK_START" || detail?.type === "BREAK_END"
    )
    ?.reduce((acc, item, index, array) => {
      if (item?.type === "BREAK_START") {
        const end = array[index + 1]?.timestamp || "Ongoing";
        acc.push(`${item.timestamp} - ${end}`);
      }
      return acc;
    }, []);

  // Group travel times
  const travelSummary = apiData?.details
    ?.filter(
      (detail) =>
        detail?.type === "TRAVEL_START" || detail?.type === "TRAVEL_END"
    )
    ?.reduce((acc, item, index, array) => {
      if (item?.type === "TRAVEL_START") {
        const end = array[index + 1]?.timestamp || "Ongoing";
        acc.push(`${item.timestamp} - ${end}`);
      }
      return acc;
    }, []);

  // Add break and travel summaries
  if (breakSummary?.length > 0) {
    transformedData.push({
      label: "Break Summary",
      icon: CoffeeIcon,
      times: breakSummary,
      type: "BREAK_SUMMARY",
    });
  }

  if (travelSummary?.length > 0) {
    transformedData.push({
      label: "Travel Summary",
      icon: CarIcon,
      times: travelSummary,
      type: "TRAVEL_SUMMARY",
    });
  }

  return transformedData;
};

// Component
const DailyStats = ({ open, onClose, onBack, workerId }) => {
  const [statsData, setStatsData] = useState(null);
  const [loader, setLoader] = useState(false);

  const date = localStorage.getItem("SelectedDate");

  useEffect(() => {
    if (open) {
      const fetchStats = async () => {
        setLoader(true);
        setStatsData(null);
        try {
          const response = await getWorkerStats(workerId, date);
          if (response?.success) {
            setStatsData(response?.data || null);
          } else {
            setStatsData(null);
          }
        } catch (error) {
          setStatsData(null);
          console.log("error", error);
        } finally {
          setLoader(false);
        }
      };
      fetchStats();
    }
  }, [open, workerId, date]);

  const transformedData = statsData
    ? transformApiData(statsData, statsData?.local_timezone)
    : [];

  console.log("transformedData", transformedData);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      className="!overflow-visible"
      sx={{
        "& .MuiDialog-paperWidthSm": {
          overflow: "visible",
        },
      }}
      style={{ marginTop: "4rem" }}
    >
      <DialogContent className="relative bg-white py-8 rounded-lg shadow-md !overflow-visible">
        {loader ? <div className="flex justify-center items-center h-[300px]">
          <Spin />
        </div>:
         <div className="w-full h-full flex flex-col items-center">
         {open && (
           <span
             className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-pointer z-50"
             onClick={onClose}
           >
             {CloseIcon}
           </span>
         )}

         {onBack && (
           <button
             type="button"
             onClick={onBack}
             className="absolute top-4 left-4 flex items-center gap-1 text-[#1E3A8A] font-medium text-[13px]"
           >
             {BackArrowIcon}
             Back
           </button>
         )}

         <div className="flex flex-col items-center gap-4 mb-6">
           <h2 className="font-semibold text-[20px] text-[#1E3A8A]">
             Daily Stats
           </h2>
           {/* <div className="w-16 h-16 rounded-full overflow-hidden">
             <img
               src="https://pnghq.com/wp-content/uploads/2023/02/employee-photo-placeholder-png-1000.png"
               alt="Profile"
               className="w-full h-full object-cover"
             />
           </div> */}
         </div>

         <div className="flex flex-col items-start gap-4 w-[70%] flex-1 overflow-auto">
           {transformedData.length > 0 ? (
             transformedData
               ?.filter((data) => !Array.isArray(data.times)) // Render only summary items
               .map((stat, index) => (
                 <div key={index} className="w-full">
                   <div className="flex items-center gap-2 text-sm">
                     <span className="flex items-center justify-center w-5 h-5 shrink-0 [&>svg]:w-5 [&>svg]:h-5">
                       {stat.icon}
                     </span>
                     <span className="font-medium text-gray-600">
                       {stat.label}
                     </span>
                     <span className="text-gray-800">
                       {stat.time || "N/A"}
                     </span>
                   </div>
                   {index < 4 && (
                     <div className="w-full h-[1px] bg-[#E9E9E9] mt-2"></div>
                   )}
                 </div>
               ))
           ) : (
             <div className="text-gray-600 text-sm text-center">
               No activity found for the specified date range.
             </div>
           )}
         </div>

         <div className="w-[70%] mt-6">
           {transformedData.length > 0 &&
           transformedData.filter((data) => Array.isArray(data.times)).length >
             0 ? (
             transformedData
               ?.filter((data) => Array.isArray(data.times)) // Render break/travel summaries
               .map((section, index) => (
                 <div key={index}>
                   <SummarySection
                     title={section.label}
                     icon={section.icon}
                     items={section.times}
                   />
                   {index < transformedData.length - 1 && (
                     <div className="w-full h-[1px] bg-[#E9E9E9] my-4"></div>
                   )}
                 </div>
               ))
           ) : (
             <div className="text-gray-600 text-sm text-center">
               {/* No summary data available for breaks or travel. */}
             </div>
           )}
         </div>
       </div>
        }
       
      </DialogContent>
    </Dialog>
  );
};

const SummarySection = ({ title, icon, items }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <span className="flex items-center justify-center w-5 h-5 shrink-0 [&>svg]:w-5 [&>svg]:h-5">
        {icon}
      </span>
      <h3 className="text-sm font-semibold text-gray-600">{title}</h3>
    </div>
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => (
        <span
          key={index}
          className="px-2 py-1 bg-[#E7F2FF] font-medium text-[13px] text-[#1E3A8A] rounded-[4px]"
        >
          {item}
        </span>
      ))}
    </div>
  </div>
);

export default DailyStats;
